import { DOCUMENT } from '@angular/common';
import { APP_ID, DestroyRef, Inject, NgModule, isDevMode, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { SwRegistrationOptions } from '@angular/service-worker';
import { BrandingService } from '@next/core-lib/branding';
import { AgrBrowserModule } from '@next/core-lib/browser';
import { AgrEnvironment, EnvironmentInjector } from '@next/core-lib/environment';
import { ENVIRONMENT_MAPPER } from '@next/core-lib/environment-browser';
import { LocaleService } from '@next/core-lib/i18n';
import { PWA_INSTALL_PROMPT_MODE } from '@next/core-lib/pwa';
import { provideStoreDevtools } from '@ngrx/store-devtools';

import { environment } from 'src/environments/environment';

import { AppComponent } from './app.component';
import { AppModule } from './app.module';
import { applyPwaBranding, getPwaAppName } from './shared/pwa/pwa-branding';
import { PwaBrandingService } from './shared/pwa/pwa-branding.service';
import { pwaRegistrationOptions } from './shared/pwa/pwa-worker';
import { loadRuntimeEnvironment } from './shared/pwa/runtime-environment';

declare global {
  var CONFIG_BUILD_ID: string | undefined;
}

const loadedConfig = signal(environment);

export const setupRuntimeConfig = (async () => {
  const resolved = await loadRuntimeEnvironment(
    environment,
    window.location.hostname,
    globalThis.CONFIG_BUILD_ID,
  );
  loadedConfig.set(resolved);
  document.title = getPwaAppName(resolved.TENANT, resolved.DEFAULT_LOCALE);
  applyPwaBranding(document, resolved.TENANT, resolved.DEFAULT_LOCALE);
})();

@NgModule({
  imports: [AppModule, AgrBrowserModule],
  bootstrap: [AppComponent],
  providers: [
    { provide: BrandingService, useClass: PwaBrandingService },
    { provide: Title, useExisting: BrandingService },
    { provide: PWA_INSTALL_PROMPT_MODE, useValue: 'native' },
    // AgrBrowserModule already registers ngsw-worker.js. Override its runtime-only gate.
    { provide: SwRegistrationOptions, useFactory: pwaRegistrationOptions },
    {
      provide: ENVIRONMENT_MAPPER,
      useFactory: () => loadedConfig(),
    },
    {
      provide: APP_ID,
      useFactory: () => loadedConfig().APPLICATION_NAME,
    },
    provideStoreDevtools({
      maxAge: 25, // Retains last 25 states
      logOnly: !isDevMode(), // Restrict extension to log-only mode in production
      autoPause: true, // Pauses recording actions when extension window is not open
      trace: false, // Stack trace for actions
      traceLimit: 75, // Maximum stack trace frames
      connectInZone: false, // Connect outside Angular zone to prevent unnecessary change detection
    }),
  ],
})
export class AppBrowserModule {
  constructor(
    @Inject(DOCUMENT) document: Document,
    @Inject(EnvironmentInjector) env: AgrEnvironment,
    locale: LocaleService,
    @Inject(BrandingService) branding: PwaBrandingService,
    destroyRef: DestroyRef,
  ) {
    locale.locale$.pipe(takeUntilDestroyed(destroyRef)).subscribe((language) => {
      applyPwaBranding(document, env.TENANT, language);
      branding.setTitle('');
    });
  }
}
